import { useReducer } from 'react';
import { Omit } from 'react-redux';
import { AnyAction } from 'redux';
import { IUseBankAccounts, TBankAccounts } from './IBankAccounts';
import { bankFormValidation } from '@funds/features/helpers/validation';
import { setAdditionalValues } from '../WithdrawForm/useWithdrawForm';
import { IUseFormPayload } from '../WithdrawForm/IWithdrawForm';

const initialValue: Omit<IUseBankAccounts, 'dispatch'> = {
  errors: { initial: 'initial error disables Next button' },
  values: {
    stepLoading: false,
    editMode: false,
  },
};

const reducer = (
  state: Omit<IUseBankAccounts, 'dispatch'>,
  action: AnyAction
) => {

  const getUpdatedData = (
    payload: IUseFormPayload,
    relatedData: IUseFormPayload[]
  ) => {
    const additionalValues = setAdditionalValues(payload.name, payload.value);
    const newValues = {
      ...state.values,
      [payload.name]: payload.value,
      ...additionalValues,
      ...relatedData.reduce((acc, curr, i) => ({ ...acc, [curr.name]: curr.value }), {}),
    } as TBankAccounts;
    const bicSwiftAsyncError = state.errors.bic_swift_async;
    const errors = bankFormValidation(newValues, payload.name === 'bic_swift');
    if (bicSwiftAsyncError && payload.name !== 'bic_swift') {
      errors.bic_swift_async = bicSwiftAsyncError;
    }

    return {
      errors,
      values: newValues,
    };
  };

  switch (action.type) {
    case 'edit':
      const data = getUpdatedData(action.payload, action.relatedData || []);
      return {
        values: data.values,
        errors: data.errors,
      };

    case 'cleanSwiftExternalError':
      const { bic_swift_external, ...rest } = state.errors;
      return { ...state, errors: { ...rest } };

    case 'editMode':
      const newValues = { ...state.values, ...action.payload };
      return {
        ...state,
        values: newValues,
        errors: { ...bankFormValidation(newValues), ...initialValue.errors },
      };

    case 'setInitial':
      return {
        errors: initialValue.errors,
        values: { ...initialValue.values, ...action.payload },
      };

    case 'setErrors':
      return { ...state, errors: action.errors };

    default:
      return state;
  }
};

export const useBankAccounts = (): IUseBankAccounts => {
  const [state, dispatch] = useReducer(reducer, { ...initialValue });

  return {
    dispatch,
    values: state.values,
    errors: state.errors,
  };
};
